import React from 'react'
import Link from 'next/link'
import { sanitySrcUrl } from '../../lib/libFunctions'
import { sectionPath } from '../../lib/sections'
import { listingImageOverride } from '../../lib/listing-image-overrides'
import { listingAnalyticsAttributes } from '../../lib/analytics-content.mjs'
import { imageStyles } from '../../styles/base/_allBase.styles'
import {
  Card,
  CardImage,
  CardBody,
  Pick,
  CardTitle,
  CardTagline,
  MetaRow,
  MetaItem,
  MetaDot,
  ViewMore,
} from './listing-card.styles'

const { ImgCover } = imageStyles

// A meta token (category, area, price) with a separating dot before all but the first.
const Meta = ({ children, first }) =>
  children ? (
    <>
      {!first && <MetaDot aria-hidden>·</MetaDot>}
      <MetaItem>{children}</MetaItem>
    </>
  ) : null

export const ListingCard = ({ listing, position, activeFilter = 'All' }) => {
  if (!listing) return null
  const href = `/${sectionPath(listing.sectionPage)}/${listing.slug}`
  const imageOverride = listingImageOverride(listing)

  // Price only shown once a listing's details are verified (same gate as the
  // detail info panel) and never the placeholder "VERIFY".
  const price =
    listing.verified && listing.priceRange && listing.priceRange !== 'VERIFY'
      ? listing.priceRange
      : null

  // Build the meta tokens in priority order, dropping any that are absent.
  const tokens = [listing.category, listing.area, price].filter(Boolean)

  return (
    <Link
      href={href}
      style={{ textDecoration: 'none' }}
      data-ga-event="listing_click"
      data-ga-item-name={listing.location}
      data-ga-item-category={listing.category || ''}
      data-ga-item-section={listing.sectionPage || ''}
      data-analytics-filter={activeFilter}
      {...listingAnalyticsAttributes(listing, position)}
    >
      <Card>
        {listing.mainImage && (
          <CardImage>
            <ImgCover
              src={imageOverride?.src || sanitySrcUrl(listing.mainImage)}
              alt={imageOverride?.alt || listing.mainImage?.alt || listing.location}
              fill
              sizes="(max-width:599px) 100vw, (max-width:899px) 50vw, 33vw"
            />
          </CardImage>
        )}
        <CardBody>
          {listing.editorsPick && <Pick>★ Editor&apos;s pick</Pick>}
          <CardTitle>{listing.location}</CardTitle>
          {tokens.length > 0 && (
            <MetaRow>
              {tokens.map((t, i) => (
                <Meta key={i} first={i === 0}>
                  {t}
                </Meta>
              ))}
            </MetaRow>
          )}
          {listing.tagline && <CardTagline>{listing.tagline}</CardTagline>}
          <ViewMore>View details →</ViewMore>
        </CardBody>
      </Card>
    </Link>
  )
}

export default ListingCard
